/**
 * Reading a rolled weapon back off disk.
 *
 * A roll is the one part of a stack that a save can lie about in an interesting way. An id can be
 * checked against the registry, a count against a stack limit, but a magnitude is just a number and
 * a name is just a string, and both go straight onto another player's screen and into the damage
 * pipeline.
 *
 * So: ids are checked, slots are checked, a roll on something that is not a rolled weapon is
 * refused, magnitudes are clamped into what the affix actually offers at that rarity, and the name
 * is re-derived rather than trusted. Clamping rather than refusing on the magnitude alone is
 * deliberate: retuning a range is a thing this repo expects to do, and a save from last week should
 * pull into the new range rather than fail to load.
 */

import { fail, succeed } from '@/sim/inventory/inventoryResult';
import { isAffixId, affix } from '@/sim/items/affixRegistry';
import { RARITY_ORDER } from '@/sim/items/itemContract';
import { quantizeMagnitude } from '@/sim/items/rolledWeaponContract';
import { rolledWeaponName } from '@/sim/items/rolledWeaponName';
import { isWeaponId } from '@/sim/weapons/gunRegistry';
import { clamp } from '@/sim/math';
import type { InventoryResult } from '@/sim/inventory/inventoryResult';
import type { AffixId, AffixSlot } from '@/sim/items/affixContract';
import type { ItemDef, Rarity } from '@/sim/items/itemContract';
import type { RolledWeapon } from '@/sim/items/rolledWeaponContract';

/** One roll as a save holds it: four strings and two numbers, none of them checked. */
export interface StoredRolledWeapon {
  readonly weaponId: string;
  readonly rarity: string;
  readonly prefixId: string;
  readonly suffixId: string;
  readonly prefixMagnitude: number;
  readonly suffixMagnitude: number;
  readonly name: string;
}

function isRarity(value: string): value is Rarity {
  return (RARITY_ORDER as readonly string[]).includes(value);
}

/** An affix id that exists and sits in the pool it is being used as. */
function readAffixId(value: string, slot: AffixSlot): InventoryResult<AffixId> {
  if (!isAffixId(value)) return fail({ reason: 'unknownAffix', affixId: value });
  if (affix(value).slot !== slot)
    return fail({ reason: 'affixSlotMismatch', affixId: value, slot });

  return succeed(value);
}

/** A magnitude pulled into what the affix offers at that rarity. Bad numbers read as the floor. */
function readMagnitude(id: AffixId, rarity: Rarity, value: number): number {
  const range = affix(id).ranges[rarity];
  if (!Number.isFinite(value)) return range.min;

  return quantizeMagnitude(clamp(value, range.min, range.max));
}

/**
 * Validates a stored roll against the def it was found on.
 *
 * Null in, null out: most stacks carry no roll, and that is not an error. A roll on anything but a
 * non-stackable weapon is, because there is nothing for it to modify.
 */
export function readRolledWeapon(
  stored: StoredRolledWeapon | null,
  def: ItemDef,
): InventoryResult<RolledWeapon | null> {
  if (stored === null) return succeed(null);
  if (def.kind !== 'weapon') return fail({ reason: 'rollOnNonWeapon', defId: def.id });
  if (!isWeaponId(stored.weaponId) || stored.weaponId !== def.weaponId) {
    return fail({ reason: 'rollOnNonWeapon', defId: def.id });
  }
  if (!isRarity(stored.rarity)) return fail({ reason: 'unknownRarity', rarity: stored.rarity });

  const prefixId = readAffixId(stored.prefixId, 'prefix');
  if (!prefixId.ok) return fail(prefixId.failure);

  const suffixId = readAffixId(stored.suffixId, 'suffix');
  if (!suffixId.ok) return fail(suffixId.failure);

  const roll = {
    weaponId: def.weaponId,
    rarity: stored.rarity,
    prefixId: prefixId.value,
    suffixId: suffixId.value,
    prefixMagnitude: readMagnitude(prefixId.value, stored.rarity, stored.prefixMagnitude),
    suffixMagnitude: readMagnitude(suffixId.value, stored.rarity, stored.suffixMagnitude),
  };

  return succeed({ ...roll, name: rolledWeaponName(roll) });
}
